import React from 'react';

import AddonIcon from '@atlaskit/icon/glyph/addon';
import Archive from '@atlaskit/icon/glyph/archive';
import BitbucketBuildsIcon from '@atlaskit/icon/glyph/bitbucket/builds';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';
import Code from '@atlaskit/icon/glyph/code';
import Component from '@atlaskit/icon/glyph/component';
import Emoji from '@atlaskit/icon/glyph/emoji';
import EmojiActivityIcon from '@atlaskit/icon/glyph/emoji/activity';
import File from '@atlaskit/icon/glyph/file';
import HighlightsIcon from '@atlaskit/icon/glyph/highlights';
import JiraTestSessionIcon from '@atlaskit/icon/glyph/jira/test-session';
import LightbulbIcon from '@atlaskit/icon/glyph/lightbulb';
import Premium from '@atlaskit/icon/glyph/premium';
import Roadmap from '@atlaskit/icon/glyph/roadmap';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Ship from '@atlaskit/icon/glyph/ship';
import Sprint from '@atlaskit/icon/glyph/sprint';
import Stopwatch from '@atlaskit/icon/glyph/stopwatch';
import WorldIcon from '@atlaskit/icon/glyph/world';

import {
  TestType,
  TestTypeColorName,
  TestTypeIconName,
} from '@qality/common-model/src/TestType';

export type Option = {
  label: string;
  value: string;
  component?: React.ElementType;
  tokens?: {
    backgroundSubtle: string;
    backgroundBold: string;
    iconSubtlest: string;
    iconSubtle: string;
    iconBold: string;
  };
};

export const DEFAULT_TEST_TYPE: TestType = {
  id: 0,
  name: 'QAlity test',
  description:
    'QAlity test is a default test type used in QAlity Plus. Jira admin can edit, add and remove existing test types in the app settings',
  colorName: 'ORANGE',
  iconName: 'MAGNIFIER',
  isDefault: true,
};

export const TEST_TYPE_COLORS: Map<TestTypeColorName, Option> = new Map<
  TestTypeColorName,
  Option
>([
  [
    'GREEN',
    {
      label: 'Green',
      value:
        'var(--ds-background-accent-green-subtle, var(--ds-fb-background-accent-green-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-green-subtler, var(--ds-fb-background-accent-green-subtler))',
        backgroundBold:
          'var(--ds-background-accent-green-subtle, var(--ds-fb-background-accent-green-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-green-subtlest, var(--ds-fb-background-accent-green-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-green, var(--ds-fb-text-accent-green))',
        iconBold:
          'var(--ds-text-accent-green-bolder, var(--ds-fb-text-accent-green-bolder))',
      },
    },
  ],
  [
    'GRAY',
    {
      label: 'Gray',
      value:
        'var(--ds-background-accent-gray-subtle, var(--ds-fb-background-accent-gray-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-gray-subtler, var(--ds-fb-background-accent-gray-subtler))',
        backgroundBold:
          'var(--ds-background-accent-gray-subtle, var(--ds-fb-background-accent-gray-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-gray-subtlest, var(--ds-fb-background-accent-gray-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-gray-bolder, var(--ds-fb-text-accent-gray-bolder))',
        iconBold:
          'var(--ds-text-accent-gray-bolder, var(--ds-fb-text-accent-gray-bolder))',
      },
    },
  ],
  [
    'BLUE',
    {
      label: 'Blue',
      value:
        'var(--ds-background-accent-blue-subtle, var(--ds-fb-background-accent-blue-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-blue-subtler, var(--ds-fb-background-accent-blue-subtler))',
        backgroundBold:
          'var(--ds-background-accent-blue-subtle, var(--ds-fb-background-accent-blue-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-blue-subtlest, var(--ds-fb-background-accent-blue-subtlest))',
        iconSubtle: 'var(--ds-text-accent-blue, var(--ds-fb-text-accent-blue))',
        iconBold:
          'var(--ds-text-accent-blue-bolder, var(--ds-fb-text-accent-blue-bolder))',
      },
    },
  ],
  [
    'PURPLE',
    {
      label: 'Purple',
      value:
        'var(--ds-background-accent-purple-subtle, var(--ds-fb-background-accent-purple-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-purple-subtler, var(--ds-fb-background-accent-purple-subtler))',
        backgroundBold:
          'var(--ds-background-accent-purple-subtle, var(--ds-fb-background-accent-purple-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-purple-subtlest, var(--ds-fb-background-accent-purple-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-purple, var(--ds-fb-text-accent-purple))',
        iconBold:
          'var(--ds-text-accent-purple-bolder, var(--ds-fb-text-accent-purple-bolder))',
      },
    },
  ],
  [
    'RED',
    {
      label: 'Red',
      value:
        'var(--ds-background-accent-red-subtle, var(--ds-fb-background-accent-red-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-red-subtler, var(--ds-fb-background-accent-red-subtler))',
        backgroundBold:
          'var(--ds-background-accent-red-subtle, var(--ds-fb-background-accent-red-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-red-subtlest, var(--ds-fb-background-accent-red-subtlest))',
        iconSubtle: 'var(--ds-text-accent-red, var(--ds-fb-text-accent-red))',
        iconBold:
          'var(--ds-text-accent-red-bolder, var(--ds-fb-text-accent-red-bolder))',
      },
    },
  ],
  [
    'TEAL',
    {
      label: 'Teal',
      value:
        'var(--ds-background-accent-teal-subtle, var(--ds-fb-background-accent-teal-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-teal-subtler, var(--ds-fb-background-accent-teal-subtler))',
        backgroundBold:
          'var(--ds-background-accent-teal-subtle, var(--ds-fb-background-accent-teal-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-teal-subtlest, var(--ds-fb-background-accent-teal-subtlest))',
        iconSubtle: 'var(--ds-text-accent-teal, var(--ds-fb-text-accent-teal))',
        iconBold:
          'var(--ds-text-accent-teal-bolder, var(--ds-fb-text-accent-teal-bolder))',
      },
    },
  ],
  [
    'YELLOW',
    {
      label: 'Yellow',
      value: 'var(--ds-chart-yellow-bold, var(--ds-fb-chart-yellow-bold))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-yellow-subtler, var(--ds-fb-background-accent-yellow-subtler))',
        backgroundBold:
          'var(--ds-background-accent-yellow-subtle, var(--ds-fb-background-accent-yellow-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-yellow-subtlest, var(--ds-fb-background-accent-yellow-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-yellow, var(--ds-fb-text-accent-yellow))',
        iconBold:
          'var(--ds-text-accent-yellow-bolder, var(--ds-fb-text-accent-yellow-bolder))',
      },
    },
  ],
  [
    'LIME',
    {
      label: 'Lime',
      value:
        'var(--ds-background-accent-lime-subtler-pressed, var(--ds-fb-background-accent-lime-subtler-pressed))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-lime-subtler, var(--ds-fb-background-accent-lime-subtler))',
        backgroundBold:
          'var(--ds-background-accent-lime-subtle, var(--ds-fb-background-accent-lime-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-lime-subtlest, var(--ds-fb-background-accent-lime-subtlest))',
        iconSubtle: 'var(--ds-text-accent-lime, var(--ds-fb-text-accent-lime))',
        iconBold:
          'var(--ds-text-accent-lime-bolder, var(--ds-fb-text-accent-lime-bolder))',
      },
    },
  ],
  [
    'ORANGE',
    {
      label: 'Orange',
      value:
        'var(--ds-background-accent-orange-subtle, var(--ds-fb-background-accent-orange-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-orange-subtler, var(--ds-fb-background-accent-orange-subtler))',
        backgroundBold:
          'var(--ds-background-accent-orange-subtle, var(--ds-fb-background-accent-orange-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-orange-subtlest, var(--ds-fb-background-accent-orange-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-orange, var(--ds-fb-text-accent-orange))',
        iconBold:
          'var(--ds-text-accent-orange-bolder, var(--ds-fb-text-accent-orange-bolder))',
      },
    },
  ],
  [
    'MAGENTA',
    {
      label: 'Magenta',
      value:
        'var(--ds-background-accent-magenta-subtle, var(--ds-fb-background-accent-magenta-subtle))',
      tokens: {
        backgroundSubtle:
          'var(--ds-background-accent-magenta-subtler, var(--ds-fb-background-accent-magenta-subtler))',
        backgroundBold:
          'var(--ds-background-accent-magenta-subtle, var(--ds-fb-background-accent-magenta-subtle))',
        iconSubtlest:
          'var(--ds-background-accent-magenta-subtlest, var(--ds-fb-background-accent-magenta-subtlest))',
        iconSubtle:
          'var(--ds-text-accent-magenta, var(--ds-fb-text-accent-magenta))',
        iconBold:
          'var(--ds-text-accent-magenta-bolder, var(--ds-fb-text-accent-magenta-bolder))',
      },
    },
  ],
]);

export const TEST_TYPE_ICONS: Map<TestTypeIconName, Option> = new Map<
  TestTypeIconName,
  Option
>([
  [
    'MAGNIFIER',
    {
      label: 'Magnifier',
      value: 'magnifier',
      component: SearchIcon,
    },
  ],
  [
    'ACTIVITY',
    {
      label: 'Activity',
      value: 'activity',
      component: EmojiActivityIcon,
    },
  ],
  [
    'ARCHIVE',
    {
      label: 'Archive',
      value: 'archive',
      component: Archive,
    },
  ],
  [
    'BUG',
    {
      label: 'Bug',
      value: 'bug',
      component: JiraTestSessionIcon,
    },
  ],
  [
    'BULB',
    {
      label: 'Bulb',
      value: 'bulb',
      component: LightbulbIcon,
    },
  ],
  [
    'CHART',
    {
      label: 'Chart',
      value: 'chart',
      component: ChildIssuesIcon,
    },
  ],
  [
    'CODE',
    {
      label: 'Code',
      value: 'code',
      component: Code,
    },
  ],
  [
    'EMOJI',
    {
      label: 'Emoji',
      value: 'emoji',
      component: Emoji,
    },
  ],
  [
    'COMPONENT',
    {
      label: 'Component',
      value: 'component',
      component: Component,
    },
  ],
  [
    'FILE',
    {
      label: 'File',
      value: 'file',
      component: File,
    },
  ],
  [
    'FLOW',
    {
      label: 'Flow',
      value: 'flow',
      component: BitbucketBuildsIcon,
    },
  ],
  [
    'GLOBE',
    {
      label: 'Globe',
      value: 'globe',
      component: WorldIcon,
    },
  ],
  [
    'HIGHLIGHT',
    {
      label: 'Highlight',
      value: 'highlight',
      component: HighlightsIcon,
    },
  ],
  [
    'PART',
    {
      label: 'Part',
      value: 'part',
      component: AddonIcon,
    },
  ],
  [
    'PREMIUM',
    {
      label: 'Premium',
      value: 'premium',
      component: Premium,
    },
  ],
  [
    'ROADMAP',
    {
      label: 'Roadmap',
      value: 'roadmap',
      component: Roadmap,
    },
  ],
  [
    'SHIP',
    {
      label: 'Ship',
      value: 'ship',
      component: Ship,
    },
  ],
  [
    'SPRINT',
    {
      label: 'Sprint',
      value: 'sprint',
      component: Sprint,
    },
  ],
  [
    'STOPWATCH',
    {
      label: 'Stopwatch',
      value: 'stopwatch',
      component: Stopwatch,
    },
  ],
]);
